// Store-detail carousel by observation (ADR-0137 Postscript): the pure
// reading of one `reportPhotoGroup` (packages/api-types/src/report-photos.ts)
// that the card, its lightbox and the gallery meta share.
//
// The verdict is the ITEM's, never a photo's: nothing here labels a single
// photo conforming or defective. The lifecycle state comes from the same
// derivation every other dashboard surface uses (`presentationalState`,
// which composes estadoKindFor / estadoFor / estadoLabelsFor), so the card
// pill can never disagree with the observation's own detail screen.

import {
  reabiertaLabel,
  type ObservationPhaseVocabulary,
  type PillTone,
  type ReportPhotoGroup,
} from "@bitacorapro/api-types";
import { photoReviewApplies } from "@bitacorapro/core-engine";
import { presentationalState, type PresentationalKind } from "./postventa/phase";

/** `.vgal__flag--*` modifiers. */
export type FlagTone = "ok" | "low" | "med" | "high" | "none" | "info";

// Shared semantic tone → gallery flag. The flag family has no separate
// peak/danger step, so those fall to `high`.
const FLAG_TONE: Record<PillTone, FlagTone> = {
  neutral: "none",
  info: "info",
  ok: "ok",
  "sev-low": "low",
  "sev-med": "med",
  "sev-high": "high",
  "sev-peak": "high",
  danger: "high",
};

/** The item verdict alone — today's per-photo badge, now read once per card. */
export function verdictFlag(g: Pick<ReportPhotoGroup, "verdict" | "itemConformance">): {
  tone: FlagTone;
  label: string;
} {
  if (g.verdict) return { tone: g.verdict.tone, label: g.verdict.label };
  if (g.itemConformance === "not_applicable") return { tone: "none", label: "No aplica" };
  return { tone: "none", label: "Sin evaluar" };
}

export type GroupPill = {
  tone: FlagTone;
  /** Severity part («Crítica»); null when only the state is known. */
  verdictLabel: string | null;
  /** Lifecycle part («Cerrada», «Reabierta ×2»); null = plain verdict pill. */
  estado: string | null;
  /** Stable code of the state part, for the icon; null with no state part. */
  kind: PresentationalKind | null;
};

// The plain entry states read as the verdict alone: an observation that
// was just raised adds nothing a manager can't infer from «Leve». On a
// graph without triage (facility, ADR-0134) the entry is
// awaiting_assignment, which derives `accepted`.
const PLAIN_KINDS: ReadonlySet<PresentationalKind> = new Set(["open", "accepted"]);

export function groupPill(
  g: Pick<ReportPhotoGroup, "verdict" | "itemConformance" | "observation">,
  vocab?: ObservationPhaseVocabulary
): GroupPill {
  const base = verdictFlag(g);
  const o = g.observation;
  if (!o) return { tone: base.tone, verdictLabel: base.label, estado: null, kind: null };
  const s = presentationalState(
    {
      observationStatus: null,
      observationPhase: o.observationPhase,
      reopened: o.reopened,
      reopenCount: o.reopenCount,
      observationClosedAt: o.observationClosedAt,
      observationClosedReason: o.observationClosedReason,
    },
    vocab
  );
  if (PLAIN_KINDS.has(s.kind)) {
    return { tone: base.tone, verdictLabel: base.label, estado: null, kind: null };
  }
  const reopened = s.kind === "reopened";
  return {
    // Still open after a reopening: the severity is what matters, so the
    // verdict's tone wins; every other state paints its own tone.
    tone: reopened ? base.tone : FLAG_TONE[s.tone],
    verdictLabel: g.verdict?.label ?? null,
    estado: reopened ? reabiertaLabel(Math.max(1, s.reopenCount)) : s.label,
    kind: s.kind,
  };
}

/** The observation was closed by its receiver («Cerrada»), not annulled. */
export function isClosedConforme(
  g: Pick<ReportPhotoGroup, "observation">,
  vocab?: ObservationPhaseVocabulary
): boolean {
  const o = g.observation;
  if (!o || o.observationPhase !== "closed") return false;
  return (
    presentationalState(
      {
        observationStatus: null,
        observationPhase: o.observationPhase,
        reopened: o.reopened,
        reopenCount: o.reopenCount,
        observationClosedAt: o.observationClosedAt,
        observationClosedReason: o.observationClosedReason,
      },
      vocab
    ).kind === "conforme"
  );
}

export function isPositiveGroup(g: Pick<ReportPhotoGroup, "verdict" | "itemConformance">): boolean {
  return g.verdict?.tone === "ok" || g.itemConformance === "positive";
}

/** A checklist item with a problem: a non-positive verdict, a negative
 *  conformance, or a materialized observation. */
export function isProblemGroup(
  g: Pick<ReportPhotoGroup, "verdict" | "itemConformance" | "observation">
): boolean {
  if (isPositiveGroup(g)) return false;
  return g.verdict !== null || g.itemConformance === "negative" || g.observation !== null;
}

export function isUnattributedGroup(g: Pick<ReportPhotoGroup, "itemKey">): boolean {
  return g.itemKey === null;
}

/**
 * Is this card's item under per-photo review at all (ADR-0169 Postscript
 * 2026-10-23)? Only when the item has an observation AND its photo set
 * has 2+ photos AND the card shows 2+ of them — core-engine's
 * photoReviewApplies, the same rule core-api's gallery applies.
 *
 * - No observation (a conforming item, n/a, no verdict): the review
 *   exists to clear an observation, so the card reads as before the
 *   review existed — its «Conforme» pill alone, never a «Propuesta IA»
 *   badge, whatever the AI proposed for its photos.
 * - A single photo IS the evidence of the item's verdict: the pill alone,
 *   no per-photo badge, no «por revisar», no decision controls. That
 *   holds for a card showing one photo of a larger set too (an ambiguous
 *   URL went to the unattributed card): nothing to pick there, and the
 *   hidden photos could never be confirmed.
 *
 * The server computes the rule and ships it as `underPhotoReview`; that
 * field wins whenever present. Its absence means a core-api deployed
 * before the field, and only then does the dashboard apply the rule on
 * its own, reading "has an observation" from the wire as the live
 * `observation` or `reviewable`. That fallback is exact only for a recipe
 * with an observation lifecycle (today the only one with the review,
 * facility): without a lifecycle `observation` is always null and
 * `reviewable` is also false for an annulled item or an open acta, so a
 * group there may lose badges the server would have shown.
 */
export function groupUnderPhotoReview(
  g: Pick<
    ReportPhotoGroup,
    "itemKey" | "photoSetSize" | "photoIds" | "observation" | "reviewable" | "underPhotoReview"
  >
): boolean {
  if (g.itemKey === null) return false;
  if (g.underPhotoReview !== undefined) return g.underPhotoReview;
  return photoReviewApplies({
    hasObservation: g.observation !== null || g.reviewable,
    distinctPhotoSet: g.photoSetSize,
    distinctShown: g.photoIds.length,
  });
}

/** Card title: the item, or the area for photos with no item. */
export function groupTitle(g: Pick<ReportPhotoGroup, "itemLabel" | "areaTag">): string {
  return g.itemLabel ?? g.areaTag ?? "Fotos de la visita";
}
